import React, { useState, useEffect, useContext, useMemo } from "react";
import Link from "next/link";

import { personalTrainerPackages, generateBuddyPackages } from "data/PersonalTrainerPackages";
import { FaDumbbell, FaFire, FaBolt, FaStar } from "react-icons/fa6";
import { RequestContext } from "context/RequestContext";
import { AnimatePresence } from "framer-motion";
import { PersonalTrainerServiceDetails } from "./PersonalTrainerServiceDetails";
import { openModal } from "service/modal-service";
import { MdInfo } from "react-icons/md";

const PEAK_PREMIUM = 10;

const categoryIcons = {
    Essential: FaDumbbell,
    Signature: FaBolt,
    Elite: FaFire
};

const categoryColors = {
    Essential: { gradient: "from-emerald-500 to-green-400", text: "text-emerald-400", border: "border-emerald-500/30", glow: "bg-emerald-500/10" },
    Signature: { gradient: "from-blue-500 to-cyan-400", text: "text-blue-400", border: "border-blue-500/30", glow: "bg-blue-500/10" },
    Elite: { gradient: "from-orange-500 to-amber-400", text: "text-orange-400", border: "border-orange-500/30", glow: "bg-orange-500/10" }
};

export const PersonalTrainerBooking = ({setIsQuickBook, openRequestForm, year, isBuddyTraining = false}) => {

    const [ isShowServiceDetails, setIsShowServiceDetails ] = useState(false);
    const [ activeCategory, setActiveCategory ] = useState("Signature");
    const [ selectedPackage, setSelectedPackage ] = useState(null);
    const [ isPeak, setIsPeak ] = useState(false);
    const [ isBuddy, setIsBuddy ] = useState(isBuddyTraining);

    const buddyPackages = useMemo(() => generateBuddyPackages(personalTrainerPackages), []);
    const activePackages = isBuddy ? buddyPackages : personalTrainerPackages;

    const { setRequestCriteria, setRequestDescription, setHeadline } = useContext(RequestContext);

    const confirmBook = ( serviceType, description) => {
        setRequestDescription(description)
        setIsQuickBook(true)
        openRequestForm(serviceType)
        setHeadline("Get my trainer & diet plan")
    }

    const quickBookPT = (serviceType, description) => {
        confirmBook( serviceType, description)
    }

    const handlePackageSelect = (pkg) => {
        setSelectedPackage(pkg.id);
        quickBookPT("Fitness Trainer", pkg.packageName);
    }

    useEffect(() => {
        setRequestCriteria([])
        setRequestDescription('')
        setIsQuickBook(false)
    }, [setIsQuickBook, setRequestCriteria, setRequestDescription])

    const categories = Object.keys(activePackages);
    const currentCategoryData = activePackages[activeCategory];
    const CategoryIcon = categoryIcons[activeCategory];
    const colors = categoryColors[activeCategory];

    const sortedPackages = [...currentCategoryData.packages];

  return (
    <>
        <div className="relative bg-slate-900 py-8 md:py-12 overflow-hidden" id="personal-training-packages">
            {/* Mesh gradient blobs */}
            <div className="absolute top-0 left-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/3 w-72 h-72 bg-amber-400/8 rounded-full blur-3xl" />
            
            <div className="relative z-10 max-w-5xl mx-auto">
                {/* Header */}
                <div className="px-4 pt-4 pb-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4">
                        <span className="text-sm">💪</span>
                        <span className="text-xs font-medium text-white/70">Packages</span>
                    </div>
                    <h2 className="text-2xl md:text-4xl font-extrabold text-white tracking-tight mb-1">
                        Personal Trainer Rates &amp; Packages in Singapore
                    </h2>
                    <p className="text-white/40 text-sm flex items-center justify-center gap-1">
                        <span>⏱</span> 1-hour sessions
                        <span className="mx-2 text-white/20">•</span>
                        <button
                            type="button"
                            aria-label="View service details"
                            className="text-orange-400 font-medium flex items-center gap-0.5 hover:text-orange-300 transition-colors"
                            onClick={() => openModal(setIsShowServiceDetails)}
                        >
                            <MdInfo className="text-sm" />
                            Details
                        </button>
                    </p>
                </div>
                

                {/* Personal / Buddy Training Toggle */}
                <div className="flex flex-col items-center gap-2 mt-5 mb-1">
                    <div className="flex items-center gap-3">
                        <span className={`font-medium transition-colors ${!isBuddy ? 'text-orange-400' : 'text-white/30'}`}>
                            🏋️1-on-1 Training
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsBuddy((prev) => !prev)}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ${
                                isBuddy ? 'bg-orange-500' : 'bg-white/20'
                            }`}
                            aria-label="Toggle buddy training"
                            role="switch"
                            aria-checked={isBuddy}
                        >
                            <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                    isBuddy ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                        <span className={`font-medium transition-colors ${isBuddy ? 'text-orange-400' : 'text-white/30'}`}>
                            👥Buddy Training
                        </span>
                    </div>

                    {/* Dynamic H3 + intro */}
                    <div className="text-center my-2">
                        <h3 className="text-lg font-semibold text-white">
                            {isBuddy ? '👥 Buddy Training' : '🏋️ 1-on-1 Personal Training'}
                        </h3>
                        <p className="text-xs text-white/40 mt-0.5 max-w-sm mx-auto">
                            {isBuddy
                                ? 'Train with a friend and each pay just 70% of the solo rate — same great trainer, shared motivation.'
                                : 'Dedicated sessions tailored entirely to your goals, with your trainer\'s full attention every minute.'}
                        </p>
                    </div>
                </div>

                {/* Category Tabs */}
                <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-white/5 px-3 py-3">
                    <div className="flex gap-2 justify-center">
                        {categories.map((category) => {
                            const Icon = categoryIcons[category];
                            const isActive = activeCategory === category;
                            const catColors = categoryColors[category];
                            return (
                                <button
                                    key={category}
                                    type="button"
                                    onClick={() => setActiveCategory(category)}
                                    className={`
                                        flex items-center gap-1.5 px-4 py-2.5 rounded-full font-medium text-sm
                                        transition-all duration-300
                                        ${isActive 
                                            ? `bg-linear-to-r ${catColors.gradient} text-white shadow-lg` 
                                            : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/70 border border-white/10'
                                        }
                                    `}
                                    aria-label={`Select ${category} trainers`}
                                >
                                    <Icon className="text-xs" />
                                    <span>{category}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Category Description */}
                <div 
                    key={activeCategory} 
                    className={`mx-3 mt-4 p-4 rounded-2xl bg-white/5 backdrop-blur-md border ${colors.border}`}
                >
                    <div className="flex items-start gap-3">
                        <CategoryIcon className={`${colors.text} text-lg shrink-0 mt-0.5`} />
                        <div>
                            <h3 className={`font-semibold ${colors.text} mb-1`}>
                                {activeCategory} Trainers
                            </h3>
                            <p className="text-white/80 text-sm leading-relaxed">
                                {currentCategoryData.description.replace(/\*/g, '').replace(/💪|⚡|🔥/g, '')}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Off-Peak / Peak Toggle */}
                <div className="flex justify-center mt-5 mb-2">
                    <div className="bg-white/5 backdrop-blur-md p-1 rounded-xl flex space-x-1 border border-white/10">
                        <button
                            type="button"
                            onClick={() => setIsPeak(false)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 relative ${
                                !isPeak
                                    ? 'bg-linear-to-r from-green-500 to-emerald-400 text-white shadow-lg'
                                    : 'text-white/40 hover:text-white/60'
                            }`}
                        >
                            ☀️ Off-Peak
                            {!isPeak && (
                                <span className="absolute -top-2 -right-2 bg-green-400 text-slate-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                                    SAVE
                                </span>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsPeak(true)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                                isPeak
                                    ? 'bg-linear-to-r from-orange-500 to-amber-400 text-white shadow-lg'
                                    : 'text-white/40 hover:text-white/60'
                            }`}
                        >
                            🕐 Peak Hours
                        </button>
                    </div>
                </div>

                {/* Time info */}
                <div className="text-center mb-3">
                    {isPeak ? (
                        <p className="text-sm text-white/40">
                            Weekends & weekday before 10am &amp; after 5pm
                        </p>
                    ) : (
                        <p className="text-sm text-green-400 font-medium">
                            🎉 Weekdays 10am – 5pm · Enjoy ${PEAK_PREMIUM}/session Off!
                        </p>
                    )}
                </div>

                {/* Packages Grid */}
                <div key={`packages-${activeCategory}`} className="px-3 py-4">
                    {/* Mobile: Horizontal scroll */}
                    {/* <div className="grid md:hidden grid-cols-2 gap-3">
                        {sortedPackages.map((pkg) => (
                            <PackageCardCompact 
                                key={pkg.id}
                                pkg={pkg}
                                colors={colors}
                                isSelected={selectedPackage === pkg.id}
                                onSelect={() => handlePackageSelect(pkg)}
                                isPeak={isPeak}
                            />
                        ))}
                    </div> */}

                    {/* Desktop: Grid layout */}
                    <div className="grid grid-cols-2 gap-3">
                        {sortedPackages.map((pkg) => (
                            <PackageCard 
                                key={pkg.id}
                                pkg={pkg}
                                colors={colors}
                                isSelected={selectedPackage === pkg.id}
                                onSelect={() => handlePackageSelect(pkg)}
                                isPeak={isPeak}
                            />
                        ))}
                    </div>
                </div>

                {/* Sentosa Disclaimer */}
                <div className="mx-3 mb-3">
                    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                        <span className="text-amber-400 text-sm shrink-0 mt-0.5">🏝️</span>
                        <p className="text-amber-300/80 text-xs leading-relaxed">
                            <strong className="text-amber-300">Sentosa locations:</strong> An additional S$40 conveyance fee applies per session for training at Sentosa Island.
                        </p>
                    </div>
                </div>

                {/* Price List Link */}
                <div className="flex justify-center pb-4 px-4">
                    <Link
                        href="/sg/personal-trainer/personal-training-price-list"
                        className="inline-flex items-center gap-1.5 text-white/40 hover:text-orange-400 text-xs font-medium transition-colors group"
                        aria-label="View full personal training price list"
                        prefetch={false}
                    >
                        <span className="group-hover:underline underline-offset-2">
                            View full price list for 1-on-1 &amp; buddy training
                        </span>
                        <span className="group-hover:translate-x-0.5 transition-transform">→</span>
                    </Link>
                </div>
            </div>
        </div>
        {isShowServiceDetails &&
            <AnimatePresence>
                <PersonalTrainerServiceDetails setIsShowServiceDetails={setIsShowServiceDetails}/> 
            </AnimatePresence>
        }
    </>
  );
};

// Compact Package Card for Mobile
const PackageCardCompact = ({ pkg, colors, isSelected, onSelect, isPeak }) => {
    const effectivePrice = isPeak ? pkg.perSessionPrice + PEAK_PREMIUM : pkg.perSessionPrice;

    return (
        <button
            type="button"
            onClick={onSelect}
            className={`
                mt-2 relative flex flex-col items-center justify-center
                w-28 h-32 rounded-2xl border transition-all duration-300
                ${isSelected 
                    ? `${colors.border} bg-white/15 scale-105` 
                    : !isPeak
                        ? 'border-green-500/20 bg-white/5 hover:bg-white/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                }
                ${pkg.recommended ? 'ring-2 ring-orange-400/60 ring-offset-1 ring-offset-slate-900' : ''}
            `}
            aria-label={`Book ${pkg.totalServices} sessions at $${effectivePrice} per session`}
        >
            {pkg.recommended && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-linear-to-r from-orange-500 to-amber-400 text-white text-[8px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                    POPULAR
                </span>
            )}
            <span className={`text-2xl font-bold ${colors.text}`}>{pkg.totalServices}</span>
            <span className="text-[10px] text-white/30 uppercase tracking-wide">Sessions</span>
            <div className="mt-2 text-center">
                <span className="text-sm font-bold text-white">${effectivePrice}</span>
                <span className="text-[9px] text-white/30 block">/session</span>
            </div>
        </button>
    );
};

// Full Package Card for Desktop
const PackageCard = ({ pkg, colors, isSelected, onSelect, isPeak }) => {
    const effectivePrice = isPeak ? pkg.perSessionPrice + PEAK_PREMIUM : pkg.perSessionPrice;
    const totalSavings = isPeak ? PEAK_PREMIUM * pkg.totalServices : 0;

    return (
        <button
            type="button"
            onClick={onSelect}
            className={`
                relative rounded-2xl border p-4 transition-all duration-300 text-left
                hover:scale-[1.02] hover:bg-white/10
                ${isSelected 
                    ? `${colors.border} bg-white/10` 
                    : !isPeak
                        ? 'border-green-500/20 bg-white/5 hover:border-green-500/30'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                }
                ${pkg.recommended ? 'ring-2 ring-orange-400/60 ring-offset-1 ring-offset-slate-900' : ''}
            `}
            aria-label={`Book ${pkg.totalServices} sessions at $${effectivePrice} per session`}
        >
            {pkg.recommended && (
                <span className="absolute -top-2.5 left-3 bg-linear-to-r from-orange-500 to-amber-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    Best Seller
                </span>
            )}
            
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <FaDumbbell className={colors.text} />
                    <span className="font-semibold text-white/90">{pkg.totalServices} Sessions</span>
                </div>
            </div>
            
            <div className="flex items-baseline gap-1 mb-2">
                <span className={`text-2xl font-bold ${colors.text}`}>${effectivePrice}</span>
                <span className="text-xs text-white/30">/session</span>
            </div>

            {isPeak && (
                <p className="text-[10px] text-green-400 mb-1">
                    💡 Switch to off-peak &amp; save ${totalSavings}
                </p>
            )}
            
            <div className="flex items-center justify-between text-xs text-white/30 pt-2 border-t border-white/5">
                <span>{pkg.packageDuration} {pkg.packageDurationUnit}</span>
                <span className="hidden sm:block capitalize">{pkg.objective}</span>
            </div>
        </button>
    );
};
